import type { RawDealItemForValidation } from '@/lib/workers/validation.worker';
import {
  DealItemWithVersions,
  GetDealItemsByDealIdQuery,
} from '@/lib/graphql/generated/graphql';
import { Store } from '@/types/store';

export type DealItemFromQuery = NonNullable<
  GetDealItemsByDealIdQuery['deal']
>['dealItemsWithVersions'][number];

const priceKeys = [
  'buyingPrice',
  'choosePrice',
  'discountedPrice',
  'listPrice',
  'potentialSalesVolume',
  'shippingPrice',
  'eeePrice',
] as const;

type PriceKey = (typeof priceKeys)[number];

const percentageKeys = [
  'chooseDiscount',
  'marginPercentage',
  'vatRate',
] as const;

type PercentageKey = (typeof percentageKeys)[number];

export function normalize(items: DealItemFromQuery[]): DealItemWithVersions[] {
  return items.map((item) => {
    const activeVersionCopy: DealItemFromQuery['activeVersion'] = {
      ...item.activeVersion,
    };
    const lastBrandVersionCopy: DealItemFromQuery['lastBrandVersion'] =
      item.lastBrandVersion
        ? {
            ...item.lastBrandVersion,
          }
        : null;
    const lastChooseVersionCopy: DealItemFromQuery['lastChooseVersion'] =
      item.lastChooseVersion
        ? {
            ...item.lastChooseVersion,
          }
        : null;

    priceKeys.forEach((k: PriceKey) => {
      const activeVal = item.activeVersion[k];
      if (typeof activeVal === 'number') activeVersionCopy[k] = activeVal / 100;
      if (lastBrandVersionCopy) {
        const brandVal = lastBrandVersionCopy[k];
        if (typeof brandVal === 'number')
          lastBrandVersionCopy[k] = brandVal / 100;
      }
      if (lastChooseVersionCopy) {
        const chooseVal = lastChooseVersionCopy[k];
        if (typeof chooseVal === 'number')
          lastChooseVersionCopy[k] = chooseVal / 100;
      }
    });

    percentageKeys.forEach((k: PercentageKey) => {
      const activeVal = item.activeVersion[k];
      if (typeof activeVal === 'number') activeVersionCopy[k] = activeVal * 100;
      if (lastBrandVersionCopy) {
        const brandVal = lastBrandVersionCopy[k];
        if (typeof brandVal === 'number')
          lastBrandVersionCopy[k] = brandVal * 100;
      }
      if (lastChooseVersionCopy) {
        const chooseVal = lastChooseVersionCopy[k];
        if (typeof chooseVal === 'number')
          lastChooseVersionCopy[k] = chooseVal * 100;
      }
    });

    return {
      ...item,
      activeVersion: {
        ...activeVersionCopy,
        updatingFields: item.activeVersion.updatingFields,
        validationErrors: item.activeVersion.validationErrors ?? [],
      },
      lastBrandVersion: lastBrandVersionCopy,
      lastChooseVersion: lastChooseVersionCopy,
    } as DealItemWithVersions;
  });
}

export function normalizeSingle(item: DealItemFromQuery): DealItemWithVersions {
  return normalize([item])[0];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Renvoie `prev` quand `next` lui est égal en profondeur, sinon `next` en y
 * réutilisant chaque sous-objet resté égal (partage structurel).
 *
 * Un item relu à l'identique garde ainsi sa référence : la map, les lignes de
 * la table et `diffHighlightableFields` court-circuitent sur `===`, et une
 * relecture complète qui n'a rien changé ne re-rend rien. Un item modifié
 * garde les références de ses parties inchangées (`lastBrandVersion`, tableaux
 * d'images…).
 *
 * Gère les valeurs JSON et les `Date` produites par `convertDateFields`.
 */
export function shareStructure<T>(next: T, prev: unknown): T {
  if (Object.is(next, prev)) return prev as T;

  if (next instanceof Date && prev instanceof Date) {
    return (next.getTime() === prev.getTime() ? prev : next) as T;
  }

  if (Array.isArray(next) && Array.isArray(prev)) {
    let same = next.length === prev.length;
    const shared = next.map((value, index) => {
      const s = shareStructure(value, prev[index]);
      if (s !== prev[index]) same = false;
      return s;
    });
    return (same ? prev : shared) as T;
  }

  if (isPlainObject(next) && isPlainObject(prev)) {
    const keys = Object.keys(next);
    let same = keys.length === Object.keys(prev).length;
    const shared: Record<string, unknown> = {};
    for (const key of keys) {
      const s = shareStructure(next[key], prev[key]);
      if (s !== prev[key] || !(key in prev)) same = false;
      shared[key] = s;
    }
    return (same ? prev : shared) as T;
  }

  return next;
}

/**
 * Merge partagé des deux chemins d'ingestion (batch SSE et subscription) :
 * conserve les dernières versions brand/choose connues quand l'entrant est
 * null, et préserve l'activeVersion d'une édition optimiste en attente tout
 * en prenant les validationErrors fraîchement reçues.
 *
 * Le résultat partage sa structure avec `prevItem` : un item inchangé est
 * renvoyé tel quel (même référence), voir {@link shareStructure}.
 */
export function mergeStreamedItem(
  normalized: DealItemWithVersions,
  prevItem: DealItemWithVersions | undefined,
  pendingIds: ReadonlySet<string>
): DealItemWithVersions {
  let merged: DealItemWithVersions = {
    ...normalized,
    lastBrandVersion:
      normalized.lastBrandVersion ?? prevItem?.lastBrandVersion ?? null,
    lastChooseVersion:
      normalized.lastChooseVersion ?? prevItem?.lastChooseVersion ?? null,
  };

  if (pendingIds.has(normalized.id) && prevItem) {
    merged = {
      ...merged,
      activeVersion: {
        ...merged.activeVersion,
        ...prevItem.activeVersion,
        validationErrors: normalized.activeVersion.validationErrors,
      },
    };
  }

  return prevItem ? shareStructure(merged, prevItem) : merged;
}

/**
 * Projette un item brut (valeurs en cents, post-convertDateFields) vers la
 * forme minimale attendue par le worker de validation, pour ne pas retenir
 * le graphe complet des items pendant toute la durée du stream.
 */
export function toRawValidationItem(
  item: DealItemWithVersions,
  store: Store
): RawDealItemForValidation {
  const av = item.activeVersion;
  const isUs = store === Store.US;
  return {
    id: item.id,
    activeVersion: {
      historyId: av.historyId,
      isSelected: av.isSelected,
      name: av.name,
      sku: av.sku,
      gtin: av.gtin,
      options: av.options,
      shopifyVariantId: av.shopifyVariantId,
      hasDescription: av.hasDescription,
      images: av.images,
      productCategory: av.productCategory,
      listPrice: av.listPrice,
      discountedPrice: av.discountedPrice,
      chooseDiscount: av.chooseDiscount,
      choosePrice: av.choosePrice,
      buyingPrice: av.buyingPrice,
      shippingPrice: av.shippingPrice,
      marginPercentage: av.marginPercentage,
      vatRate: isUs ? 0 : av.vatRate,
      hasVatMargin: isUs ? false : av.hasVatMargin,
      stockQuantity: av.stockQuantity,
      sourceUrl: av.sourceUrl,
      madeIn: av.madeIn,
      productType: av.productType,
      couponExpirationDate: av.couponExpirationDate,
      couponTerms: av.couponTerms,
      couponHowToUse: av.couponHowToUse,
    },
  };
}
